import axios from "axios";

/**
 * Fetch with automatic retry and exponential backoff
 * Designed for mobile networks, iOS Safari, Android Chrome, and sleeping backends
 * 
 * FIXED: Proper retry termination, clear error states, no infinite loops
 * FIXED: Do NOT use withCredentials for public endpoints (CORS compatibility)
 * Retry delays: 1000ms, 2000ms, 4000ms (exponential backoff)
 */
export async function fetchWithRetry(url, options = {}) {
  const {
    maxRetries = 3,
    timeout = 25000, // 25 seconds timeout (mobile-safe, backend warmup)
    initialDelay = 1000, // Start with 1s for cold starts
    onRetry = null, // Callback when retrying
    params = {},
    withCredentials = false, // Default FALSE for CORS compatibility with wildcard origins
  } = options;

  let lastError = null;
  let lastErrorCode = null;
  let lastStatus = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Create abort controller with proper cleanup
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, timeout);

      const response = await axios.get(url, {
        params,
        withCredentials, // Only set true when explicitly needed (auth endpoints)
        signal: controller.signal,
        timeout,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'X-Site-Origin':
            typeof window !== 'undefined'
              ? window.location.origin
              : 'https://cheepsweepz.shop',
          'X-DHC-Hub': 'digitalhustlerscaribbean.com',
        },
      });

      clearTimeout(timeoutId);
      
      // SUCCESS - return immediately
      return { 
        success: true, 
        data: response.data, 
        attempt,
        status: response.status,
      };
    } catch (error) {
      lastError = error;
      lastErrorCode = error.code || 'UNKNOWN';
      lastStatus = error.response?.status || 0;
      
      // Log for debugging (will show in browser console)
      console.warn(`[fetchWithRetry] Attempt ${attempt}/${maxRetries} failed:`, {
        url,
        errorCode: lastErrorCode,
        status: lastStatus,
        message: error.message,
      });

      // Don't retry on 4xx errors (client errors) - these are intentional
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        return { 
          success: false, 
          error, 
          attempt, 
          isClientError: true,
          errorCode: lastErrorCode,
          status: lastStatus,
        };
      }

      // ONLY retry if we haven't hit max attempts
      if (attempt < maxRetries) {
        if (onRetry) {
          onRetry(attempt, maxRetries);
        }
        // Exponential backoff: 1s, 2s, 4s
        const delay = initialDelay * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      // If this was the last attempt, fall through to return error
    }
  }

  // FINAL FAILURE - all retries exhausted, return with detailed error info
  return { 
    success: false, 
    error: lastError, 
    attempt: maxRetries, 
    isClientError: false,
    errorCode: lastErrorCode,
    status: lastStatus,
    retriesExhausted: true,
  };
}

/**
 * POST request with retry (for cart operations, etc.)
 */
export async function postWithRetry(url, data = {}, options = {}) {
  const {
    maxRetries = 2,
    timeout = 15000,
    initialDelay = 500,
    onRetry = null,
    withCredentials = true,
  } = options;

  let lastError = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await axios.post(url, data, {
        withCredentials,
        timeout,
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
      });

      return { success: true, data: response.data, attempt };
    } catch (error) {
      lastError = error;
      
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        return { success: false, error, attempt, isClientError: true };
      }

      if (attempt < maxRetries) {
        if (onRetry) {
          onRetry(attempt, maxRetries);
        }
        const delay = initialDelay * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  return { success: false, error: lastError, attempt: maxRetries, isClientError: false };
}

/**
 * Wake up backend with a ping request
 * Returns object with status for debugging
 */
export async function wakeBackend(apiUrl) {
  const result = { success: false, endpoint: null, error: null };
  
  try {
    const response = await axios.get(`${apiUrl}/ping`, {
      timeout: 15000,
      headers: { 'Cache-Control': 'no-cache' },
    });
    result.success = response.status === 200;
    result.endpoint = '/ping';
    return result;
  } catch (error) {
    result.error = error.message;
    // Try the /health endpoint as fallback
    try {
      const response = await axios.get(`${apiUrl}/health`, {
        timeout: 15000,
        headers: { 'Cache-Control': 'no-cache' },
      });
      result.success = response.status === 200;
      result.endpoint = '/health';
      result.error = null;
      return result;
    } catch (e) {
      result.error = e.message;
      return result;
    }
  }
}

/**
 * Get diagnostic info for debugging API issues
 */
export function getDiagnosticInfo(apiUrl, lastError = null, lastStatus = null) {
  return {
    apiBaseUrl: apiUrl,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    online: navigator.onLine,
    lastErrorCode: lastError?.code || 'N/A',
    lastStatus: lastStatus || 'N/A',
    lastErrorMessage: lastError?.message || 'N/A',
  };
}

export default fetchWithRetry;
